import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { RiskmaptreeActions } from './riskmaptree.actions';
import { RiskMapTreeService } from '../../service/riskmaptree.service';
import { GetRiskMapTreeQuery, HandleRiskMapTreeMutation } from '../../../../graphql/generated';
import { EvaluationTreeActions } from '../evaluation-tree/evaluation-tree.actions';
import { MutationData } from '../../shared/graphql-mutation-response.util';
import { MessageService } from 'primeng/api';
import { graphQlErrorMessage } from '../../shared/graphql-error.util';


@Injectable()
export class RiskmaptreeEffects {

  loadRiskMapTree$ = createEffect(
    (rs: RiskMapTreeService = inject(RiskMapTreeService)) => {
      return this.actions$.pipe(

        ofType(RiskmaptreeActions.loadRiskMapTree),
        mergeMap(({ campaignId, legalEntityId, parameters, fields, au }) =>
          rs.getRiskMapTree(campaignId, legalEntityId, parameters, au).pipe(
            map((riskMap) => {
              return RiskmaptreeActions.loadRiskMapTreeSuccess({
                riskMap: riskMap as GetRiskMapTreeQuery['riskMapTreeByLegalEntityIdAndCampaignId'],
                fields
              });
            }),
            catchError((error) => of(RiskmaptreeActions.loadRiskMapTreeFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllRiskMapTree$ = createEffect(
    (rs: RiskMapTreeService = inject(RiskMapTreeService)) => {
      return this.actions$.pipe(
        ofType(RiskmaptreeActions.loadAllRiskMapTree),
        mergeMap(({ campaignId, legalEntityId, parameters, fields, au, param }) =>
          rs.getRiskMapTreeFilters(campaignId, legalEntityId, au).pipe(
            mergeMap((riskMap) => {
              const successAction = RiskmaptreeActions.loadAllRiskMapTreeSuccess({
                riskMap: riskMap,
                fields
              });
              return [successAction];
            }),
            catchError((error) =>
              of(RiskmaptreeActions.loadAllRiskMapTreeFailure({ error }))
            ),
            mergeMap((result) => [
              result,
              EvaluationTreeActions.isLegalEntityEvaluationMassiveEditInProgress({ campaignId, legalEntityId })
            ])
          )
        )
      );
    },
    { functional: true }
  );

  handleRiskMapTree$ = createEffect(
    (
      rs: RiskMapTreeService = inject(RiskMapTreeService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(
        ofType(RiskmaptreeActions.handleRiskMapTree),
        mergeMap(({ campaignId, legalEntityId, processId, riskId, riskMapId, value, cptIds, parameterName, parameters, filter }) =>
          rs.handleRiskMapTree(riskMapId, campaignId, legalEntityId, processId, riskId, value, filter).pipe(
            mergeMap((riskMap) => [
              RiskmaptreeActions.handleRiskMapTreeSuccess({
                riskMap: riskMap as MutationData<HandleRiskMapTreeMutation['handleRiskMapTree']>,
                cptIds,
                parameterName
              })
            ]),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Risk Map Error',
                detail: graphQlErrorMessage(error, 'Unable to update the risk map'),
                life: 10000
              });
              return of(RiskmaptreeActions.handleRiskMapTreeFailure({ error, cptIds, parameterName }));
            }),
            mergeMap((result) => [
              result,
              EvaluationTreeActions.isLegalEntityEvaluationMassiveEditInProgress({ campaignId, legalEntityId })
            ])
          )
        )
      );
    },
    { functional: true }
  );

  createRiskMapTree$ = createEffect(
    (rs: RiskMapTreeService = inject(RiskMapTreeService)) => {
      return this.actions$.pipe(

        ofType(RiskmaptreeActions.createRiskMapTree),
        mergeMap((props) =>
          rs.createRiskMapTree(props.campaignId, props.legalEntityId, props.processId, props.riskId).pipe(
            map((riskMap) => {
              return RiskmaptreeActions.createRiskMapTreeSuccess({
                riskMap: riskMap,
                parameterName: props.parameterName,
                cptIds: props.cptId
              });
            }),
            catchError((error) => of(RiskmaptreeActions.createRiskMapTreeFailure({ error, cptIds: props.cptIds, parameterName: props.parameterName })))
          )
        )
      );
    },
    { functional: true }
  );

  /**
   * Preflight di `updateTreeAu`: senza audit entity impattate la mutation parte subito,
   * altrimenti l'impatto viene messo nello store e il componente chiede conferma.
   */
  checkTreeAuImpact$ = createEffect(
    (
      rs: RiskMapTreeService = inject(RiskMapTreeService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(

        ofType(RiskmaptreeActions.checkTreeAuImpact),
        mergeMap(({ update }) =>
          rs.treeAuImpact(update.campaignId, update.legalEntityId, update.processId).pipe(
            map((impacts) => impacts.length === 0
              ? RiskmaptreeActions.updateTreeAu(update)
              : RiskmaptreeActions.checkTreeAuImpactSuccess({ impacts, update })
            ),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Auditable Universe Error',
                detail: graphQlErrorMessage(error, 'Unable to check the auditable universe impact'),
                life: 10000
              });
              return of(RiskmaptreeActions.checkTreeAuImpactFailure({ error, cptIds: update.cptIds, parameterName: update.parameterName }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  updateTreeAu$ = createEffect(
    (
      rs: RiskMapTreeService = inject(RiskMapTreeService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(

        ofType(RiskmaptreeActions.updateTreeAu),
        mergeMap(({ campaignId, legalEntityId, processId, cptIds, parameterName, filter }) =>
          rs.updateTreeAu(campaignId, legalEntityId, processId, filter).pipe(
            map((riskMap) => {
              return RiskmaptreeActions.updateTreeAuSuccess({
                riskMap: riskMap,
                cptIds,
                parameterName
              });
            }),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Auditable Universe Error',
                detail: graphQlErrorMessage(error, 'Unable to update the auditable universe'),
                life: 10000
              });
              return of(RiskmaptreeActions.updateTreeAuFailure({ error, cptIds, parameterName }));
            })
          )
        )
      );
    },
    { functional: true }
  );


  updateTreeStatus$ = createEffect(
    (
      rs: RiskMapTreeService = inject(RiskMapTreeService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(

        ofType(RiskmaptreeActions.updateTreeStatus),
        mergeMap(({ campaignId, cptIds, legalEntityId, parameterName, processId, state, filter }) =>
          rs.updateTreeStatus(campaignId, legalEntityId, processId, state, filter).pipe(
            map((riskMap) => {
              return RiskmaptreeActions.updateTreeStatusSuccess({
                riskMap: riskMap,
                cptIds,
                parameterName
              });
            }),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Status Error',
                detail: graphQlErrorMessage(error, 'Unable to update the status'),
                life: 10000
              });
              return of(RiskmaptreeActions.updateTreeStatusFailure({ error, cptIds, parameterName }));
            })
          )
        )
      );
    },
    { functional: true }
  );

  updateTreeNote$ = createEffect(
    (
      rs: RiskMapTreeService = inject(RiskMapTreeService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(

        ofType(RiskmaptreeActions.updateTreeNote),
        mergeMap(({ campaignId, legalEntityId, processCode, cptIds, parameterName, note, filter, au, param, parameters, fields }) =>
          rs.updateTreeNote(campaignId, legalEntityId, processCode, note, filter).pipe(
            mergeMap((riskMap) => [
              RiskmaptreeActions.updateTreeNoteSuccess({
                riskMap,
                cptIds,
                parameterName,
                note
              }),
              RiskmaptreeActions.loadAllRiskMapTree({
                au,
                campaignId,
                legalEntityId,
                parameters,
                param,
                fields
              })
            ]),
            catchError((error) => {
              messageService.add({
                severity: 'error',
                summary: 'Update Note Error',
                detail: graphQlErrorMessage(error, 'Unable to update the note'),
                life: 10000
              });
              return of(
                RiskmaptreeActions.updateTreeNoteFailure({
                  error,
                  cptIds,
                  parameterName
                })
              );
            })
          )
        )

      );
    },
    { functional: true }
  );

  exportRiskmap$ = createEffect(
    (rs: RiskMapTreeService = inject(RiskMapTreeService)) => {
      return this.actions$.pipe(

        ofType(RiskmaptreeActions.exportRiskMapTree),
        mergeMap(({ campaignId, legalEntityId, parameters, timeZone }) =>
          rs.exportRiskmapTree(campaignId, legalEntityId, parameters, timeZone
          ).pipe(
            map((riskMap) => {
              this.downloadFile(riskMap!.data![0]!.content, riskMap!.data![0]!.filename)
              return RiskmaptreeActions.exportRiskMapTreeSuccess({
                riskMap
              });
            }),
            catchError((error) => of(RiskmaptreeActions.exportRiskMapTreeFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  async downloadFile(response: string, fileName: string | undefined) {
    const typeFormat = {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    };
    const link = window.document.createElement("a");
    const blob = this.base64ToBlob(response, typeFormat.type); // Cambia il tipo MIME
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName ?? "fileExport";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }

  constructor(private actions$: Actions) { }
}

